import { useState } from 'react'
import { X, AlertCircle, Save, TrendingUp } from 'lucide-react'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { api } from '../lib/axios'

interface Student {
  id: string
  firstName: string
  lastName: string
  class?: {
    name: string
  }
}

interface EvaluationModalProps {
  student: Student
  onClose: () => void
  onSuccess: () => void
  existingEvaluation?: any
}

const CRITERIA = [
  { 
    key: 'respectRegles', 
    label: 'Respect des règles',
    description: 'Respecte le règlement intérieur et les consignes'
  },
  { 
    key: 'respectAutres', 
    label: 'Respect des autres',
    description: 'Fait preuve de civilité envers ses pairs et adultes'
  },
  { 
    key: 'ecoute', 
    label: 'Écoute',
    description: 'Prend en compte les conseils et remarques'
  },
  { 
    key: 'travail', 
    label: 'Travail',
    description: 'Fait les efforts nécessaires pour progresser'
  },
  { 
    key: 'autonomie', 
    label: 'Autonomie',
    description: 'Travaille de manière autonome et responsable'
  },
  { 
    key: 'soin', 
    label: 'Soin',
    description: 'Apporte du soin au travail fourni'
  }
]

const LEVELS = [
  { value: 1, label: 'Insuffisant', color: 'bg-red-500', textColor: 'text-red-700' },
  { value: 2, label: 'Fragile', color: 'bg-orange-500', textColor: 'text-orange-700' },
  { value: 3, label: 'Satisfaisant', color: 'bg-blue-500', textColor: 'text-blue-700' },
  { value: 4, label: 'Très bon', color: 'bg-green-500', textColor: 'text-green-700' }
]

const PERIODS = [
  'Trimestre 1',
  'Trimestre 2',
  'Trimestre 3',
  'Semestre 1',
  'Semestre 2',
  'Année complète'
]

export function EvaluationModal({ 
  student, 
  onClose, 
  onSuccess,
  existingEvaluation 
}: EvaluationModalProps) {
  const [formData, setFormData] = useState({
    respectRegles: existingEvaluation?.respectRegles || null,
    respectAutres: existingEvaluation?.respectAutres || null,
    ecoute: existingEvaluation?.ecoute || null,
    travail: existingEvaluation?.travail || null,
    autonomie: existingEvaluation?.autonomie || null,
    soin: existingEvaluation?.soin || null,
    period: existingEvaluation?.period || PERIODS[0],
    evaluationDate: existingEvaluation?.evaluationDate 
      ? new Date(existingEvaluation.evaluationDate).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleCriteriaChange = (key: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key as keyof typeof prev] === value ? null : value
    }))
  }

  const calculateAverage = () => {
    const values = Object.values(formData)
      .slice(0, 6)
      .filter(v => v !== null) as number[]
    
    if (values.length === 0) return 0
    return (values.reduce((sum, val) => sum + val, 0) / values.length).toFixed(1)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const payload = {
        studentId: student.id,
        ...formData,
        // Convertir null en undefined pour Zod
        respectRegles: formData.respectRegles || undefined,
        respectAutres: formData.respectAutres || undefined,
        ecoute: formData.ecoute || undefined,
        travail: formData.travail || undefined,
        autonomie: formData.autonomie || undefined,
        soin: formData.soin || undefined
      }

      if (existingEvaluation) {
        await api.put(`/api/evaluations/${existingEvaluation.id}`, payload)
      } else {
        await api.post('/api/evaluations', payload)
      }

      onSuccess()
    } catch (err: any) {
      console.error('❌ Erreur sauvegarde évaluation:', err)
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  const average = calculateAverage()
  const averageColor = 
    Number(average) >= 3.5 ? 'text-green-600' :
    Number(average) >= 2.5 ? 'text-blue-600' :
    Number(average) >= 1.5 ? 'text-orange-600' :
    'text-red-600'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {existingEvaluation ? 'Modifier l\'évaluation' : 'Nouvelle évaluation comportementale'}
            </h2>
            <p className="text-gray-600 mt-1">
              Élève : <strong>{student.firstName} {student.lastName}</strong>
              {student.class && ` • ${student.class.name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Moyenne en temps réel */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Moyenne comportementale</p>
                  <p className="text-xs text-gray-500">
                    {Object.values(formData).slice(0, 6).filter(v => v !== null).length} / 6 critères évalués
                  </p>
                </div>
              </div>
              <div className={`text-5xl font-bold ${averageColor}`}>
                {average}<span className="text-2xl">/4</span>
              </div>
            </div>
          </div>

          {/* Métadonnées */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Période */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Période *
              </label>
              <select
                required
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                {PERIODS.map(period => (
                  <option key={period} value={period}>{period}</option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date d'évaluation *
              </label>
              <input
                type="date"
                required
                value={formData.evaluationDate}
                onChange={(e) => setFormData({ ...formData, evaluationDate: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Critères d'évaluation */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Critères comportementaux (1 à 4)
            </h3>

            {CRITERIA.map(criteria => {
              const currentValue = formData[criteria.key as keyof typeof formData] as number | null
              
              return (
                <div key={criteria.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h4 className="font-semibold text-gray-900">{criteria.label}</h4>
                    <p className="text-sm text-gray-600">{criteria.description}</p>
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    {LEVELS.map(level => {
                      const isSelected = currentValue === level.value
                      
                      return (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() => handleCriteriaChange(criteria.key, level.value)}
                          className={`
                            p-4 rounded-lg border-2 transition-all
                            ${isSelected 
                              ? `${level.color} border-gray-800 text-white shadow-lg scale-105` 
                              : 'border-gray-300 hover:border-gray-400 bg-white'
                            }
                          `}
                        >
                          <div className="text-2xl font-bold mb-1">{level.value}</div>
                          <div className={`text-xs font-medium ${
                            isSelected ? 'text-white' : level.textColor
                          }`}>
                            {level.label}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-200">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-lg transition-colors"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Sauvegarde...' : existingEvaluation ? 'Modifier' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
